import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { InputField } from "@/components/podkit/forms/InputField";
import { Text } from "@/components/podkit/typography/Text";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { TrackLocations } from "@/hooks/use-segment";
import { useListSCIMConfigurations, type PlainSCIMConfiguration } from "@/queries/scim-queries";
import { SCIMConfigCard } from "@/routes/settings/login/SCIMConfigCard";
import { ConfigureSCIM } from "@/routes/settings/login/ConfigureSCIM";
import { RegenerateSCIMTokenModal } from "@/routes/settings/login/RegenerateSCIMTokenModal";
import { SCIMTokenInput } from "@/routes/settings/login/SCIMTokenInput";
import { useCallback, useState, type FC } from "react";

type ModalState =
    | { type: "none" }
    | { type: "configure"; config?: PlainSCIMConfiguration }
    | { type: "confirm-delete"; config: PlainSCIMConfiguration }
    | { type: "display-token"; config: PlainSCIMConfiguration; token: string }
    | { type: "regenerate-token"; config: PlainSCIMConfiguration };

type SCIMSectionProps = {
    organizationId: string;
    editable: boolean;
};

export const SCIMSection: FC<SCIMSectionProps> = ({ organizationId, editable }) => {
    const { data: configs = [], isLoading, error } = useListSCIMConfigurations();
    const [modalState, setModalState] = useState<ModalState>({ type: "none" });
    const hasConfigs = configs.length > 0;

    const handleCloseModal = useCallback(() => {
        setModalState({ type: "none" });
    }, []);

    const handleEdit = useCallback((config: PlainSCIMConfiguration) => {
        setModalState({ type: "configure", config });
    }, []);

    const handleDelete = useCallback((config: PlainSCIMConfiguration) => {
        setModalState({ type: "confirm-delete", config });
    }, []);

    const handleRegenerateToken = useCallback((config: PlainSCIMConfiguration) => {
        setModalState({ type: "regenerate-token", config });
    }, []);

    const handleNewConfig = useCallback(() => {
        setModalState({ type: "configure" });
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <Heading3>SCIM Provisioning</Heading3>
                <Text className="text-base text-content-secondary">
                    Automatically manage users and groups from your identity provider.
                </Text>
            </div>

            {isLoading && (
                <Card variant="bordered" className="bg-surface-glass p-4">
                    <Text className="text-content-tertiary">Loading SCIM configurations...</Text>
                </Card>
            )}

            {error && (
                <Card variant="bordered" className="bg-surface-glass p-4">
                    <Text className="text-content-danger">Failed to load SCIM configurations</Text>
                </Card>
            )}

            {!isLoading && !error && !hasConfigs && (
                <Card variant="bordered" className="flex flex-col items-start gap-4 bg-surface-glass p-6 text-left">
                    <div className="flex flex-col gap-1">
                        <Text className="text-base font-medium text-content-primary">SCIM is not currently configured</Text>
                        <Text className="text-base text-content-secondary">
                            Connect your identity provider to automatically create and manage users and groups.
                        </Text>
                    </div>
                    <Button
                        variant="primary"
                        disabled={!editable}
                        onClick={handleNewConfig}
                        data-tracking-id="new-scim-configuration-empty"
                    >
                        Setup now
                    </Button>
                </Card>
            )}

            {!isLoading &&
                !error &&
                configs.map((config) => (
                    <SCIMConfigCard
                        key={config.id}
                        config={config}
                        editable={editable}
                        onEdit={() => handleEdit(config)}
                        onDelete={() => handleDelete(config)}
                        onRegenerateToken={() => handleRegenerateToken(config)}
                    />
                ))}

            {modalState.type !== "none" && (
                <Dialog open onOpenChange={(open) => !open && handleCloseModal()}>
                    <DialogContent data-track-location={TrackLocations.SCIMConfiguration}>
                        {(modalState.type === "configure" || modalState.type === "confirm-delete") && (
                            <ConfigureSCIM
                                organizationId={organizationId}
                                config={modalState.config}
                                initialState={modalState.type === "confirm-delete" ? "confirm-delete" : "configure"}
                                onClose={handleCloseModal}
                            />
                        )}
                        {modalState.type === "regenerate-token" && (
                            <RegenerateSCIMTokenModal
                                config={modalState.config}
                                onClose={handleCloseModal}
                                onTokenRegenerated={(token) => {
                                    setModalState({ type: "display-token", config: modalState.config, token });
                                }}
                            />
                        )}
                        {modalState.type === "display-token" && modalState.token && (
                            <RegeneratedTokenDisplay
                                token={modalState.token}
                                onClose={handleCloseModal}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

/**
 * Component to display regenerated token (shown only once)
 */
const RegeneratedTokenDisplay: FC<{ token: string; onClose: () => void }> = ({ token, onClose }) => {
    const scimEndpoint = `${window.location.origin}/scim/v2`;

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <>
            <DialogHeader>
                <DialogTitle>Token regenerated</DialogTitle>
                <DialogDescription>Copy these values into your identity provider to keep SCIM working.</DialogDescription>
            </DialogHeader>

            <DialogBody className="flex flex-col gap-4">
                <InputField label="SCIM endpoint URL">
                    <CopyableInput value={scimEndpoint} disabled data-tracking-id="copy-scim-endpoint-regenerated" />
                </InputField>

                <InputField label="Token">
                    <SCIMTokenInput value={token} copyTrackingId="copy-scim-token-regenerated" />
                </InputField>

                <Text className="text-sm text-content-tertiary">
                    Treat this token like a password. It is shown only once.
                </Text>
                <Banner variant="info" text="The previous token has been invalidated and will no longer work." />
            </DialogBody>

            <DialogFooter>
                <div className="flex-grow" />
                <Button variant="primary" onClick={handleClose} data-tracking-id="close-regenerated-token-display">
                    Done
                </Button>
            </DialogFooter>
        </>
    );
};
